import { Link, createFileRoute, notFound } from "@tanstack/react-router";

import { ArticleBody } from "~/components/cih/article-body";
import {
  CihAtAGlanceCard,
  type AtAGlanceRow,
} from "~/components/cih/at-a-glance-card";
import { CihClosingCta } from "~/components/cih/closing-cta";
import type { CihCoachCellData } from "~/components/cih/coach-cell";
import { CihCoachGrid } from "~/components/cih/coach-grid";
import { FRAMEWORK_TYPE_META } from "~/components/cih/constants";
import { CihFaqSection } from "~/components/cih/faq-section";
import { CihHero } from "~/components/cih/hero";
import {
  CihProgramRows,
  type ProgramRowData,
} from "~/components/cih/program-rows";
import { CihPullQuoteBand } from "~/components/cih/pull-quote-band";
import { RelatedEntityPills } from "~/components/cih/related-entity-pills";
import { CihSection } from "~/components/cih/section";
import { CihSectionHeader } from "~/components/cih/section-header";
import { CihStatStrip } from "~/components/cih/stat-strip";
import { CihTabNav, type CihTab } from "~/components/cih/tab-nav";
import { Button } from "~/components/ui/button";
import {
  getCoachesByFrameworkFn,
  getPublishedFrameworkWithRelationsFn,
  type FrameworkWithRelations,
} from "~/lib/functions/cih/frameworks";
import type { FrameworkType } from "~/lib/schemas/db-enums-cih";
import {
  annotateBodyHeadings,
  type BodyHeading,
  dropRestatedOpeningHeading,
} from "~/lib/shared/cih/body-toc";
import { buildFrameworkJsonLd } from "~/lib/shared/cih/framework-jsonld";
import { buildStatStrip } from "~/lib/shared/cih/stat-strip";
import { createBrandMeta } from "~/lib/shared/meta";

type CoachesResult = Awaited<ReturnType<typeof getCoachesByFrameworkFn>>;
type CoachRow = CoachesResult["coaches"][number];

type LoaderData = {
  title: string;
  framework: FrameworkWithRelations;
  coaches: CoachRow[];
  coachesTotal: number;
  /** Tiptap doc with stable `id` attrs on every heading. */
  bodyDoc: FrameworkWithRelations["body"];
  /** Ordered H2 anchors for the sticky nav. */
  bodyHeadings: BodyHeading[];
};

export const Route = createFileRoute("/_public/frameworks/$slug/")({
  staticData: { title: "Framework" },
  loader: async ({ params }): Promise<LoaderData> => {
    const framework = await getPublishedFrameworkWithRelationsFn({
      data: { slug: params.slug },
    });
    if (!framework) throw notFound();
    const coachesResult = await getCoachesByFrameworkFn({
      data: { frameworkId: framework.id, page: 1, limit: 6 },
    });
    const { doc: bodyDoc, headings: bodyHeadings } = annotateBodyHeadings(
      dropRestatedOpeningHeading(framework.body),
    );
    return {
      title: framework.name,
      framework,
      coaches: coachesResult.coaches,
      coachesTotal: coachesResult.total,
      bodyDoc,
      bodyHeadings,
    };
  },
  head: ({
    loaderData,
    params,
  }: {
    loaderData?: LoaderData;
    params: { slug: string };
  }) => {
    const framework = loaderData?.framework;
    const url = `https://dreamcoachmatch.com/frameworks/${params.slug}`;
    if (!framework) {
      return createBrandMeta({
        title: "Coaching Framework",
        description: "Details about this coaching framework.",
        ogUrl: url,
        canonical: url,
      });
    }

    const typeMeta =
      FRAMEWORK_TYPE_META[framework.type as FrameworkType] ??
      FRAMEWORK_TYPE_META.framework;

    // A framework entry is an evergreen reference Article — mirror that in OG
    // (og:type=article + article:* timestamps) so it matches the Article node
    // in the JSON-LD graph below rather than defaulting to og:type=website.
    const base = createBrandMeta({
      title: `${framework.name} | Coaching Framework`,
      description:
        framework.description || "Details about this coaching framework.",
      ogUrl: url,
      canonical: url,
      ogType: "article",
      section: typeMeta.label,
      ...(framework.publishedAt
        ? { publishedTime: framework.publishedAt.toISOString() }
        : {}),
      ...((framework.updatedAt ?? framework.publishedAt)
        ? {
            modifiedTime: (
              framework.updatedAt ?? framework.publishedAt
            )?.toISOString(),
          }
        : {}),
    });

    const graph = buildFrameworkJsonLd({
      slug: params.slug,
      name: framework.name,
      description: framework.description,
      typeLabel: typeMeta.label,
      cco: framework.cco
        ? { name: framework.cco.name, slug: framework.cco.slug }
        : null,
      faqs: framework.faqs ?? [],
      publishedAt: framework.publishedAt,
      updatedAt: framework.updatedAt,
    });

    return {
      ...base,
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(graph) },
      ],
    };
  },
  component: FrameworkDetailPage,
});

function FrameworkDetailPage() {
  const { framework, coaches, coachesTotal, bodyDoc, bodyHeadings } =
    Route.useLoaderData();
  const { slug } = Route.useParams();

  const typeMeta =
    FRAMEWORK_TYPE_META[framework.type as FrameworkType] ??
    FRAMEWORK_TYPE_META.framework;

  const atAGlanceRows: AtAGlanceRow[] = [
    { key: "Type", value: typeMeta.label },
    {
      key: "Developed by",
      value: framework.cco?.name ?? null,
      href: framework.cco?.slug
        ? `/organizations/${framework.cco.slug}`
        : undefined,
    },
  ];

  const statCells = buildStatStrip({
    narrativeStats: framework.narrativeStats,
    hidePlatformStats: framework.hidePlatformStats,
    derivedStats: [
      {
        value: framework.ctps.length > 0 ? String(framework.ctps.length) : null,
        label: "Programs teaching",
      },
      {
        value:
          framework.challenges.length > 0
            ? String(framework.challenges.length)
            : null,
        label: "Challenges related",
      },
    ],
  });

  const faqs = framework.faqs ?? [];

  const challengePills = framework.challenges
    .filter(
      (c): c is (typeof framework.challenges)[number] & { slug: string } =>
        c.slug != null,
    )
    .map((c) => ({ label: c.title, href: `/coaching-for/${c.slug}` }));

  const programRows: ProgramRowData[] = framework.ctps.map((ctp) => ({
    id: ctp.id,
    slug: ctp.slug,
    name: ctp.name,
    logoUrl: null,
    deliveryFormat: ctp.deliveryFormat,
    accreditationLevel: ctp.accreditationLevel,
    duration: ctp.duration,
    tuitionRange: null,
  }));

  const coachCells: CihCoachCellData[] = coaches.map((c) => ({
    id: c.id,
    username: c.username,
    fullName: c.fullName,
    profilePictureUrl: c.profilePictureUrl,
    credential: c.credential,
    description: c.description,
  }));

  const overviewTab: CihTab = {
    id: "overview",
    label: "Overview",
    count: framework.body ? undefined : 0,
  };
  const sectionTabs: CihTab[] = [
    {
      id: "approach",
      label: "Approach",
      count: framework.pullQuote ? undefined : 0,
    },
    { id: "related", label: "Applied to", count: challengePills.length },
    { id: "training", label: "Training", count: programRows.length },
    {
      id: "coaches",
      label: "Coaches",
      count: coachesTotal > 0 ? undefined : 0,
    },
    { id: "faq", label: "FAQ", count: faqs.length },
  ];

  // A body heading that repeats a tab the template already renders would sit
  // beside it as a second, identical pill pointing at a different anchor.
  const templateTabLabels = new Set(
    [overviewTab, ...sectionTabs].map((tab) => tab.label.toLowerCase()),
  );
  const bodyHeadingTabs: CihTab[] = bodyHeadings
    .filter((heading) => !templateTabLabels.has(heading.label.toLowerCase()))
    .map((heading) => ({ id: heading.id, label: heading.label }));

  const tabs: CihTab[] = [overviewTab, ...bodyHeadingTabs, ...sectionTabs];

  return (
    <>
      <CihHero
        breadcrumb={[
          { label: "Home", href: "/" },
          { label: "Frameworks", href: "/frameworks" },
          { label: framework.name, href: `/frameworks/${slug}` },
        ]}
        kicker={typeMeta.label}
        kickerColorClass={typeMeta.text}
        title={framework.name}
        lede={framework.description}
        cta={
          <Link to="/discovery" search={{ source: `framework:${slug}` }}>
            <Button variant="primary">Find a coach →</Button>
          </Link>
        }
        meta={<CihAtAGlanceCard rows={atAGlanceRows} />}
      />

      <CihStatStrip cells={statCells} />
      <CihTabNav tabs={tabs} />

      {framework.body ? (
        <CihSection id="overview" divider={false}>
          <CihSectionHeader label="Overview" title="About" />
          <div className="mx-auto max-w-7xl px-4 xl:px-0">
            <ArticleBody
              body={bodyDoc}
              className="prose prose-lg prose-a:text-purple-700"
            />
          </div>
        </CihSection>
      ) : null}

      {framework.pullQuote ? (
        <div id="approach">
          <CihPullQuoteBand
            quote={framework.pullQuote}
            attribution={framework.pullQuoteAttribution}
          />
        </div>
      ) : null}

      {challengePills.length > 0 ? (
        <CihSection id="related" divider={!framework.pullQuote}>
          <RelatedEntityPills
            items={challengePills}
            sectionLabel="Applied to"
            heading="Challenges where coaches use this"
          />
        </CihSection>
      ) : null}

      {programRows.length > 0 ? (
        <CihSection id="training">
          <CihProgramRows
            programs={programRows}
            sectionLabel="Training programs"
            heading={`Training programs that teach ${framework.name}`}
          />
        </CihSection>
      ) : null}

      {coachCells.length > 0 ? (
        <CihSection id="coaches">
          <CihCoachGrid
            coaches={coachCells}
            totalCount={coachesTotal}
            sectionLabel="Coaches"
            heading={`Coaches who use ${framework.name}`}
            seeAllHref={`/frameworks/${slug}/coaches`}
          />
        </CihSection>
      ) : null}

      {faqs.length > 0 ? (
        <CihSection id="faq">
          <CihFaqSection
            faqs={faqs}
            heading={`Questions about ${framework.name}`}
          />
        </CihSection>
      ) : null}

      <CihClosingCta
        headline={`Work with a coach who uses ${framework.name}.`}
        sub="Free · 5 minutes · No commitment"
        cta={
          <Link to="/discovery" search={{ source: `framework:${slug}` }}>
            <Button variant="primary" size="lg">
              Start →
            </Button>
          </Link>
        }
      />
    </>
  );
}
